import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  createManufacturingEquipment,
  deleteManufacturingEquipmentImage,
  fetchManufacturingEquipment,
  fetchManufacturingEquipmentImageUrl,
  fetchNextEquipmentCode,
  updateManufacturingEquipment,
  uploadManufacturingEquipmentImage,
} from '../../api/manufacturingEquipment';
import {
  ManufacturingEquipment,
  ManufacturingEquipmentPayload,
  EquipmentType,
} from '../../types/manufacturingEquipment';
import ImageLightbox from '../common/ImageLightbox';
import '../contract/ContractForm.css';
import '../product/ProductForm.css';

import { reportError } from '../../utils/notify';
interface ManufacturingEquipFormProps {
  mode: 'create' | 'edit';
  equipmentId?: number;
  equipmentType?: EquipmentType;
  onCancel: () => void;
  onSaved: (saved: ManufacturingEquipment) => void;
}

const EMPTY: ManufacturingEquipmentPayload = {
  equipmentCode: '',
  equipmentName: '',
  manufacturer: '',
  installDate: '',
  managerName: '',
  performance: '',
  powerKw: '',
  voltage: '',
  location: '',
  ownershipType: 'OWNED',
  rentalVendor: '',
  rentalStartDate: '',
  rentalEndDate: '',
  calibCycleMonths: undefined,
  calibWarnDays: 30,
  useYn: 'Y',
  memo: '',
};

function ManufacturingEquipForm({
  mode,
  equipmentId,
  equipmentType = 'MANUFACTURING',
  onCancel,
  onSaved,
}: ManufacturingEquipFormProps) {
  const isEdit = mode === 'edit';
  const title = equipmentType === 'INSPECTION' ? '검사설비' : '제조설비';
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<ManufacturingEquipmentPayload>(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasExistingImage, setHasExistingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [equipmentCodeHint, setEquipmentCodeHint] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    if (isEdit && equipmentId) {
      return;
    }
    let cancelled = false;
    const loadSuggestion = async () => {
      try {
        setForm(EMPTY);
        setEquipmentCodeHint('');
        const suggestion = await fetchNextEquipmentCode(equipmentType);
        if (cancelled) return;
        const last = suggestion.lastValue?.trim() || '';
        const suggested = suggestion.suggestedValue?.trim() || '';
        setForm((prev) => ({ ...prev, equipmentCode: suggested }));
        setEquipmentCodeHint(
          last
            ? `직전 설비번호: ${last} → 끝자리 증가값을 제안했습니다. 필요하면 수정하세요.`
            : '이 회사의 첫 설비번호입니다. 규칙을 정해 입력하세요.'
        );
      } catch {
        if (!cancelled) {
          setForm(EMPTY);
          setEquipmentCodeHint('최근 설비번호를 불러오지 못했습니다. 직접 입력하세요.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    void loadSuggestion();
    return () => {
      cancelled = true;
    };
  }, [isEdit, equipmentId, equipmentType]);

  useEffect(() => {
    if (!isEdit || !equipmentId) {
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchManufacturingEquipment(equipmentId, equipmentType);
        if (cancelled) return;
        setForm({
          equipmentCode: data.equipmentCode ?? '',
          equipmentName: data.equipmentName,
          manufacturer: data.manufacturer ?? '',
          installDate: data.installDate ?? '',
          managerName: data.managerName ?? '',
          performance: data.performance ?? '',
          powerKw: data.powerKw ?? '',
          voltage: data.voltage ?? '',
          location: data.location ?? '',
          ownershipType: data.ownershipType === 'RENTAL' ? 'RENTAL' : 'OWNED',
          rentalVendor: data.rentalVendor ?? '',
          rentalStartDate: data.rentalStartDate ?? '',
          rentalEndDate: data.rentalEndDate ?? '',
          calibCycleMonths: data.calibCycleMonths ?? undefined,
          calibWarnDays: data.calibWarnDays ?? 30,
          useYn: data.useYn ?? 'Y',
          memo: data.memo ?? '',
        });
        setHasExistingImage(Boolean(data.hasImage));
        setRemoveExistingImage(false);
        setPendingImage(null);
        setEquipmentCodeHint('');
        if (data.hasImage) {
          objectUrl = await fetchManufacturingEquipmentImageUrl(equipmentId, equipmentType);
          if (!cancelled) setPreviewUrl(objectUrl);
        } else {
          setPreviewUrl(null);
        }
        setError('');
      } catch {
        if (!cancelled) reportError(setError, '설비를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [isEdit, equipmentId, equipmentType]);

  useEffect(() => {
    return () => {
      if (previewUrl && pendingImage) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, pendingImage]);

  const handlePickImage = (file: File | null) => {
    if (!file) return;
    if (previewUrl && pendingImage) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPendingImage(file);
    setRemoveExistingImage(false);
    setPreviewUrl(window.URL.createObjectURL(file));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const name = form.equipmentName.trim();
    if (!name) {
      reportError(setError, '설비명을 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      const isRental = form.ownershipType === 'RENTAL';
      const payload: ManufacturingEquipmentPayload = {
        equipmentCode: form.equipmentCode?.trim() || undefined,
        equipmentName: name,
        manufacturer: form.manufacturer?.trim() || undefined,
        installDate: form.installDate || null,
        managerName: form.managerName?.trim() || undefined,
        performance: form.performance?.trim() || undefined,
        powerKw: form.powerKw?.trim() || undefined,
        voltage: form.voltage?.trim() || undefined,
        location: form.location?.trim() || undefined,
        useYn: form.useYn === 'N' ? 'N' : 'Y',
        memo: form.memo?.trim() || undefined,
        ...(equipmentType === 'INSPECTION'
          ? {
              ownershipType: isRental ? 'RENTAL' : 'OWNED',
              rentalVendor: isRental ? form.rentalVendor?.trim() || null : null,
              rentalStartDate: isRental ? form.rentalStartDate || null : null,
              rentalEndDate: isRental ? form.rentalEndDate || null : null,
              calibCycleMonths:
                form.calibCycleMonths != null && Number(form.calibCycleMonths) > 0
                  ? Number(form.calibCycleMonths)
                  : null,
              calibWarnDays:
                form.calibWarnDays != null && Number(form.calibWarnDays) >= 0
                  ? Number(form.calibWarnDays)
                  : 30,
            }
          : {}),
      };
      let saved =
        isEdit && equipmentId
          ? await updateManufacturingEquipment(equipmentId, payload, equipmentType)
          : await createManufacturingEquipment(payload, equipmentType);

      if (removeExistingImage && hasExistingImage && !pendingImage && saved.equipmentId) {
        await deleteManufacturingEquipmentImage(saved.equipmentId, equipmentType);
        saved = { ...saved, hasImage: false, imageName: null };
      }
      if (pendingImage) {
        saved = await uploadManufacturingEquipmentImage(saved.equipmentId, pendingImage, equipmentType);
      }
      onSaved(saved);
    } catch {
      reportError(setError, isEdit ? '설비 수정에 실패했습니다.' : '설비 등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const showPreview = Boolean(previewUrl) && !(removeExistingImage && !pendingImage);

  return (
    <div className="contract-form-page contract-form-page--embedded">
      <header className="contract-form-page__header">
        <div>
          <p className="contract-form-page__eyebrow">설비대장</p>
          <h1>{isEdit ? `${title} 수정` : `${title} 등록`}</h1>
          <p>설비번호·제작사·제원·사진을 입력합니다.</p>
        </div>
        <div className="contract-form-page__header-actions">
          <button type="button" className="contract-form-page__ghost" onClick={onCancel}>
            취소
          </button>
        </div>
      </header>

      <div className="contract-form-page__main">
        {loading ? (
          <p className="contract-form-page__hint">불러오는 중...</p>
        ) : (
          <form className="contract-form-page__form" onSubmit={handleSubmit}>
            {error && <p className="contract-form-page__error">{error}</p>}

            <section className="contract-form-page__card">
              <div className="contract-form-page__card-head">
                <h2>기본 정보</h2>
                <span>설비 이력 카드</span>
              </div>
              <div className="contract-form-page__grid">
                <label>
                  설비 번호
                  <input
                    value={form.equipmentCode ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, equipmentCode: e.target.value }))
                    }
                    placeholder="예: DJL-H001"
                    maxLength={100}
                  />
                  {!isEdit && equipmentCodeHint ? (
                    <span
                      className="contract-form-page__hint"
                      style={{ display: 'block', marginTop: 4, fontSize: 12 }}
                    >
                      {equipmentCodeHint}
                    </span>
                  ) : null}
                </label>
                <label>
                  설비 명 *
                  <input
                    value={form.equipmentName}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, equipmentName: e.target.value }))
                    }
                    maxLength={255}
                    required
                  />
                </label>
                <label>
                  제작사
                  <input
                    value={form.manufacturer ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, manufacturer: e.target.value }))
                    }
                    maxLength={255}
                  />
                </label>
                <label>
                  설치 일자
                  <input
                    type="date"
                    value={form.installDate ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, installDate: e.target.value }))
                    }
                  />
                </label>
                <label>
                  관리 담당자
                  <input
                    value={form.managerName ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, managerName: e.target.value }))
                    }
                    maxLength={100}
                  />
                </label>
                <label>
                  위치
                  <input
                    value={form.location ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                    maxLength={255}
                  />
                </label>
                <label>
                  사용
                  <select
                    value={form.useYn ?? 'Y'}
                    onChange={(e) => setForm((prev) => ({ ...prev, useYn: e.target.value }))}
                  >
                    <option value="Y">사용</option>
                    <option value="N">미사용</option>
                  </select>
                </label>
              </div>
            </section>

            {equipmentType === 'INSPECTION' && (
              <>
                <section className="contract-form-page__card">
                  <div className="contract-form-page__card-head">
                    <h2>소유 / 대여</h2>
                  </div>
                  <div className="contract-form-page__grid">
                    <label>
                      구분
                      <select
                        value={form.ownershipType === 'RENTAL' ? 'RENTAL' : 'OWNED'}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, ownershipType: e.target.value }))
                        }
                      >
                        <option value="OWNED">자체 보유</option>
                        <option value="RENTAL">대여</option>
                      </select>
                    </label>
                    {form.ownershipType === 'RENTAL' && (
                      <>
                        <label>
                          대여처
                          <input
                            value={form.rentalVendor ?? ''}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, rentalVendor: e.target.value }))
                            }
                            maxLength={255}
                          />
                        </label>
                        <label>
                          대여 시작일
                          <input
                            type="date"
                            value={form.rentalStartDate ?? ''}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, rentalStartDate: e.target.value }))
                            }
                          />
                        </label>
                        <label>
                          대여 종료일
                          <input
                            type="date"
                            value={form.rentalEndDate ?? ''}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, rentalEndDate: e.target.value }))
                            }
                          />
                        </label>
                      </>
                    )}
                  </div>
                </section>

                <section className="contract-form-page__card">
                  <div className="contract-form-page__card-head">
                    <h2>교정 설정</h2>
                  </div>
                  <div className="contract-form-page__grid">
                    <label>
                      교정 주기 (개월)
                      <input
                        type="number"
                        min={1}
                        value={form.calibCycleMonths ?? ''}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            calibCycleMonths: e.target.value ? Number(e.target.value) : null,
                          }))
                        }
                        placeholder="예: 12"
                      />
                    </label>
                    <label>
                      만료 임박 알림 (일)
                      <input
                        type="number"
                        min={0}
                        value={form.calibWarnDays ?? 30}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            calibWarnDays: e.target.value ? Number(e.target.value) : 30,
                          }))
                        }
                        placeholder="기본 30"
                      />
                    </label>
                  </div>
                </section>
              </>
            )}

            <section className="contract-form-page__card">
              <div className="contract-form-page__card-head">
                <h2>제원 및 사양</h2>
              </div>
              <div className="contract-form-page__grid">
                <label>
                  성능
                  <input
                    value={form.performance ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, performance: e.target.value }))
                    }
                    placeholder="예: 60HZ 230W"
                  />
                </label>
                <label>
                  동력 (kW)
                  <input
                    value={form.powerKw ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, powerKw: e.target.value }))}
                  />
                </label>
                <label>
                  전원
                  <input
                    value={form.voltage ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, voltage: e.target.value }))}
                    placeholder="예: 220V"
                  />
                </label>
              </div>
            </section>

            <section className="contract-form-page__card">
              <div className="contract-form-page__card-head">
                <h2>설비 사진</h2>
              </div>
              <div className="product-form__image">
                <div
                  className={`product-form__image-preview${
                    showPreview && previewUrl ? ' image-lightbox-trigger' : ''
                  }`}
                  role={showPreview && previewUrl ? 'button' : undefined}
                  tabIndex={showPreview && previewUrl ? 0 : undefined}
                  title={showPreview && previewUrl ? '클릭하여 확대' : undefined}
                  onClick={() => {
                    if (showPreview && previewUrl) setLightboxOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (!(showPreview && previewUrl)) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setLightboxOpen(true);
                    }
                  }}
                >
                  {showPreview && previewUrl ? (
                    <img src={previewUrl} alt="설비 미리보기" />
                  ) : (
                    <span>사진 없음</span>
                  )}
                </div>
                <div className="product-form__image-actions">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={(e) => handlePickImage(e.target.files?.[0] ?? null)}
                  />
                  <label onClick={() => fileRef.current?.click()}>사진 선택</label>
                  {(showPreview || hasExistingImage) && (
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => {
                        if (pendingImage && previewUrl) {
                          window.URL.revokeObjectURL(previewUrl);
                        }
                        setPendingImage(null);
                        setPreviewUrl(null);
                        setRemoveExistingImage(true);
                        if (fileRef.current) fileRef.current.value = '';
                      }}
                    >
                      사진 제거
                    </button>
                  )}
                </div>
                <p className="product-form__hint">등록 후 상세에서 확인할 수 있습니다.</p>
              </div>
            </section>

            <section className="contract-form-page__card">
              <div className="contract-form-page__card-head">
                <h2>비고</h2>
              </div>
              <div className="contract-form-page__grid is-single">
                <label className="contract-form-page__memo">
                  <textarea
                    aria-label="비고"
                    value={form.memo ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, memo: e.target.value }))}
                    rows={4}
                    maxLength={2000}
                    placeholder="특이사항, 취급 주의 등을 입력하세요."
                  />
                </label>
              </div>
            </section>

            <div className="contract-form-page__actions">
              <button type="submit" className="contract-form-page__primary" disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </div>
      <ImageLightbox
        src={lightboxOpen && showPreview ? previewUrl : null}
        alt="설비 미리보기"
        onClose={closeLightbox}
      />
    </div>
  );
}

export default ManufacturingEquipForm;
